import { translateBackend } from '@assets/i18n/config';
import type { TranslatedString } from '@interfaces/shared';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { Badge } from '@ovComponents/2-component/badge/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@ovComponents/2-component/table/table';
import { SwitchField } from '@ovComponents/3-pattern/switchField/switchField';
import { isHouseholdModelPortfolioAssetClassStrategy } from '@ovComponents/4-module/widgets/householdModelPortfolio/householdModelPortfolio.allocation.utils';
import type { HouseholdModelPortfolioModel } from '@ovComponents/4-module/widgets/householdModelPortfolio/householdModelPortfolio.types';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { formatMoneyValue } from '@utils/moneyFormatting';
import { Alert, Box, Typography, useTheme } from 'hephaestus/web';
import { round } from 'lodash-es';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  type HoldingsProblem,
  holdingsProblem,
} from '../householdModelPortfolioDraft/householdAllocations';
import {
  type AllocationComparisonRow,
  allocationChange,
  allocationComparisonRows,
  allocationDelta,
  allocationSideValueCents,
  allocationTotal,
  allocationTotalValueCents,
  changedAllocationCount,
  isAllocationChanged,
} from './allocationComparison';

const TEST_ID = 'review-allocations';

export type ReviewAllocationsOptions = {
  title?: TranslatedString;
  subtitle?: TranslatedString;
  displayCurrentAllocations?: boolean;
  approveButton?: TranslatedString;
} & Record<string, any>;

export type ReviewAllocationsVisualProps = {
  options: ReviewAllocationsOptions;
  proposed: HouseholdModelPortfolioModel;
  /** Absent when the workflow never learnt what the household had before. */
  current?: HouseholdModelPortfolioModel;
  /**
   * What the household holds today, which the target weights are read against.
   * Absent when the balance could not be read, in which case the table stays on
   * weights alone.
   */
  householdMarketValueCents?: number;
  /** Currency symbol for the household balance, e.g. `$` or `€`. */
  currency?: string;
  loading?: boolean;
  onApprove: () => void | Promise<void>;
};

const weight = (value?: number) =>
  value == null ? '—' : `${round(value, 2)}%`;

/**
 * Always to the cent, whole amount or not: a money column is read down, and
 * figures that drop their decimals break the alignment the reader scans by.
 */
const money = (cents?: number, currency = '$') =>
  cents == null ? '—' : formatMoneyValue(cents, currency, 2, false);

/**
 * Why the change cannot be sent, in the words of the thing the advisor would
 * have to go back and fix.
 */
const BLOCKED_COPY: Record<HoldingsProblem | 'MISSING_CURRENT', string> = {
  EMPTY: 'nothingProposed',
  DUPLICATE: 'duplicateHolding',
  WEIGHTS: 'doesNotAddUp',
  GROUP_HOLDINGS: 'groupsDoNotAddUp',
  GROUP_WEIGHTS: 'groupWeightsDoNotAddUp',
  MISSING_CURRENT: 'missingCurrent',
};

/**
 * The holdings being proposed, against the ones the household has today.
 *
 * Continuing from here sends the proposal for signature, so this is the last
 * place the change can be read whole — which is why a holding being taken away
 * is shown as plainly as one being added, and why weights that no longer
 * describe a whole model stop the step rather than the service.
 */
export const ReviewAllocationsVisual = ({
  options,
  proposed,
  current,
  householdMarketValueCents,
  currency = '$',
  loading = false,
  onApprove,
}: ReviewAllocationsVisualProps) => {
  const { t } = useTranslation('workflowCompletions');
  const showsCurrent =
    options?.displayCurrentAllocations !== false && !!current;

  const rows = useMemo(
    () =>
      allocationComparisonRows(proposed, showsCurrent ? current : undefined),
    [proposed, current, showsCurrent],
  );
  const grouped = rows.some((row) => !!row.assetClassName);
  /** Neither model has a holding to its name, so there is no table to draw. */
  const nothingToRead = rows.length === 0;
  // A proposal is signed against what the household holds today, and
  // product-service will not take one that cannot say what that was. The
  // advisor can do nothing about it here, so it is said before they read the
  // change rather than after they approve it.
  const blocked =
    holdingsProblem(proposed) ?? (current ? undefined : 'MISSING_CURRENT');
  // An asset class model's weights are read within their group, so a figure
  // totalling them across the model would be a hundred per group and mean
  // nothing. The check above still speaks for whether they are whole.
  const showsTotal = !isHouseholdModelPortfolioAssetClassStrategy(proposed);

  return (
    <WorkflowCompletionForm
      onSubmit={onApprove}
      testId={`${TEST_ID}-form`}
      submitButtonProps={{
        label: translateBackend(options?.approveButton) || t('continue'),
        disabled: loading || !!blocked,
        dataTestId: `${TEST_ID}-approve-button`,
      }}
    >
      <Typography variant='displayLarge' sx={{ mt: 1 }}>
        {translateBackend(options?.title)}
      </Typography>
      {options?.subtitle && (
        <Typography variant='bodyLarge' sx={{ mb: 3 }}>
          <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
            {translateBackend(options?.subtitle)}
          </ReactMarkdown>
        </Typography>
      )}

      {nothingToRead ? (
        <Typography
          variant='bodyMedium'
          sx={{ fontWeight: 500 }}
          data-testid={`${TEST_ID}-empty`}
        >
          {t('householdModelPortfolioAllocations.empty')}
        </Typography>
      ) : (
        <AllocationTable
          rows={rows}
          showsCurrent={showsCurrent}
          householdMarketValueCents={householdMarketValueCents}
          currency={currency}
          grouped={grouped}
          showsTotal={showsTotal}
        />
      )}

      {/* A proposal emptied of everything the household held still has rows to
          read — one per holding being taken away — so the table is no
          explanation for a button that will not press. */}
      {blocked && !(blocked === 'EMPTY' && nothingToRead) && (
        <Alert severity='warning' sx={{ mt: 2 }}>
          <Typography
            variant='bodyMedium'
            sx={{ fontWeight: 500 }}
            data-testid={`${TEST_ID}-blocked`}
          >
            {t(`householdModelPortfolioAllocations.${BLOCKED_COPY[blocked]}`)}
          </Typography>
        </Alert>
      )}
    </WorkflowCompletionForm>
  );
};

/**
 * A holding whose weight the change leaves alone is only there to be read past,
 * so the ones that move are the ones the table spends its emphasis on: a tinted
 * row, a signed difference, and — where the holding itself comes or goes — a
 * badge, none of which is carried by colour alone.
 */
const AllocationTable = ({
  rows,
  showsCurrent,
  householdMarketValueCents,
  currency,
  grouped,
  showsTotal,
}: {
  rows: AllocationComparisonRow[];
  showsCurrent: boolean;
  householdMarketValueCents?: number;
  currency: string;
  grouped: boolean;
  showsTotal: boolean;
}) => {
  const { t } = useTranslation('workflowCompletions');
  const theme = useTheme();
  const [onlyChanges, setOnlyChanges] = useState(false);
  const label = (key: string) => t(`householdModelPortfolioAllocations.${key}`);

  // Without today's weights beside them there is no change to read, only a
  // list of what is being proposed.
  const changed = showsCurrent ? changedAllocationCount(rows) : 0;
  const readable =
    onlyChanges && showsCurrent ? rows.filter(isAllocationChanged) : rows;
  // A weight has no amount to be shown as until the household's balance is
  // known, and a household holding nothing values every row at nothing: columns
  // of $0.00 the reader has to look past to reach the weights.
  const showsValues =
    householdMarketValueCents != null && householdMarketValueCents > 0;
  // Shared across the full model (not the filtered rows) so a holding hidden by
  // "only show changes" still keeps its share of the cents that add up under
  // the column.
  const currentValueById = useMemo(() => {
    const values = allocationSideValueCents(
      rows,
      'current',
      householdMarketValueCents,
    );
    return new Map(
      rows.map((row, index) => [row.financialProductId, values[index]]),
    );
  }, [rows, householdMarketValueCents]);
  const proposedValueById = useMemo(() => {
    const values = allocationSideValueCents(
      rows,
      'proposed',
      householdMarketValueCents,
    );
    return new Map(
      rows.map((row, index) => [row.financialProductId, values[index]]),
    );
  }, [rows, householdMarketValueCents]);
  const valueOf = (cents?: number) => money(cents, currency);

  return (
    <>
      {showsCurrent && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mb: 1,
          }}
        >
          <Typography
            variant='bodySmall'
            sx={{ color: theme.color.onSurfaceVariant }}
            data-testid={`${TEST_ID}-change-summary`}
          >
            {changed === 0
              ? label('noChanges')
              : t('householdModelPortfolioAllocations.changeCount', {
                  count: changed,
                  total: rows.length,
                })}
          </Typography>
          {/* Nothing to narrow down to when every holding moves, and nothing to
              narrow down when none of them do. */}
          {changed > 0 && changed < rows.length && (
            <SwitchField
              size='small'
              label={label('onlyChanges')}
              checked={onlyChanges}
              onChange={setOnlyChanges}
              data-testid={`${TEST_ID}-only-changes`}
            />
          )}
        </Box>
      )}

      {/* The balance every amount on the table is a share of. It belongs to the
          household rather than to any one column, so it is said here instead of
          as a row among the holdings. */}
      {showsValues && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'baseline',
            gap: 1,
            mb: 1,
          }}
        >
          <Typography
            variant='bodyMedium'
            sx={{ color: theme.color.positive, fontWeight: 'bold' }}
          >
            {label('currentBalance')}
          </Typography>
          <Typography
            variant='bodyMedium'
            sx={{ color: theme.color.positive, fontWeight: 'bold' }}
            data-testid={`${TEST_ID}-balance`}
          >
            {money(householdMarketValueCents, currency)}
          </Typography>
        </Box>
      )}

      <Table data-testid={`${TEST_ID}-table`}>
        <TableHead>
          <TableRow>
            <TableCell isFirst bold>
              {label('security')}
            </TableCell>
            {grouped && <TableCell bold>{label('assetClass')}</TableCell>}
            {/* Like beside like. The two weights are what the reader compares,
                and the two amounts likewise; a side's weight and amount are the
                same fact told twice, so putting those two together would only
                hold the comparison apart. A weight column sitting beside a
                money column has to say which one it is; on its own it does
                not. */}
            {showsCurrent && (
              <TableCell bold number>
                {label(showsValues ? 'currentPercent' : 'current')}
              </TableCell>
            )}
            <TableCell bold number>
              {label(showsValues ? 'proposedPercent' : 'proposed')}
            </TableCell>
            {showsCurrent && showsValues && (
              <TableCell bold number>
                {label('currentValue')}
              </TableCell>
            )}
            {showsValues && (
              <TableCell bold number>
                {label('proposedValue')}
              </TableCell>
            )}
            {showsCurrent && (
              <TableCell bold number>
                {label('change')}
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {readable.map((row) => {
            const change = showsCurrent ? allocationChange(row) : 'UNCHANGED';
            const moved = change !== 'UNCHANGED';

            return (
              <TableRow
                key={row.financialProductId}
                data-testid={`${TEST_ID}-row-${row.financialProductId}`}
                backgroundColor={
                  moved ? theme.color.surfaceContainer : undefined
                }
              >
                <TableCell isFirst>
                  {row.name}
                  {row.ticker && (
                    <Box component='span' sx={{ ml: 1, opacity: 0.6 }}>
                      {row.ticker}
                    </Box>
                  )}
                  {(change === 'ADDED' || change === 'REMOVED') && (
                    <Box component='span' sx={{ ml: 1 }}>
                      <Badge
                        label={label(
                          change === 'ADDED' ? 'newHolding' : 'removedHolding',
                        )}
                        color={change === 'ADDED' ? 'positive' : 'negative'}
                        testId={`${TEST_ID}-badge-${row.financialProductId}`}
                      />
                    </Box>
                  )}
                </TableCell>
                {grouped && <TableCell>{row.assetClassName ?? '—'}</TableCell>}
                {showsCurrent && (
                  <TableCell
                    number
                    data-testid={`${TEST_ID}-current-${row.financialProductId}`}
                  >
                    {weight(row.current)}
                  </TableCell>
                )}
                <TableCell
                  number
                  bold={moved}
                  data-testid={`${TEST_ID}-proposed-${row.financialProductId}`}
                >
                  {weight(row.proposed)}
                </TableCell>
                {showsCurrent && showsValues && (
                  <TableCell
                    number
                    data-testid={`${TEST_ID}-current-value-${row.financialProductId}`}
                  >
                    {valueOf(currentValueById.get(row.financialProductId))}
                  </TableCell>
                )}
                {showsValues && (
                  <TableCell
                    number
                    bold={moved}
                    data-testid={`${TEST_ID}-proposed-value-${row.financialProductId}`}
                  >
                    {valueOf(proposedValueById.get(row.financialProductId))}
                  </TableCell>
                )}
                {showsCurrent && (
                  <TableCell
                    number
                    data-testid={`${TEST_ID}-change-${row.financialProductId}`}
                  >
                    <AllocationDelta delta={allocationDelta(row)} />
                  </TableCell>
                )}
              </TableRow>
            );
          })}
          {/* Totalled over every holding, not only the ones on screen: what the
              filter leaves out is still part of the model being proposed. */}
          {showsTotal && (
            <TableRow data-testid={`${TEST_ID}-total`}>
              <TableCell isFirst bold>
                {label('total')}
              </TableCell>
              {grouped && <TableCell />}
              {showsCurrent && (
                <TableCell bold number data-testid={`${TEST_ID}-current-total`}>
                  {weight(allocationTotal(rows, 'current'))}
                </TableCell>
              )}
              <TableCell bold number data-testid={`${TEST_ID}-proposed-total`}>
                {weight(allocationTotal(rows, 'proposed'))}
              </TableCell>
              {showsCurrent && showsValues && (
                <TableCell
                  bold
                  number
                  data-testid={`${TEST_ID}-current-value-total`}
                >
                  {money(
                    allocationTotalValueCents(
                      rows,
                      'current',
                      householdMarketValueCents,
                    ),
                    currency,
                  )}
                </TableCell>
              )}
              {showsValues && (
                <TableCell
                  bold
                  number
                  data-testid={`${TEST_ID}-proposed-value-total`}
                >
                  {money(
                    allocationTotalValueCents(
                      rows,
                      'proposed',
                      householdMarketValueCents,
                    ),
                    currency,
                  )}
                </TableCell>
              )}
              {showsCurrent && (
                <TableCell bold number data-testid={`${TEST_ID}-change-total`}>
                  <AllocationDelta
                    delta={round(
                      allocationTotal(rows, 'proposed') -
                        allocationTotal(rows, 'current'),
                      2,
                    )}
                  />
                </TableCell>
              )}
            </TableRow>
          )}
        </TableBody>
      </Table>
    </>
  );
};

/**
 * The difference between the two weights on a row, signed and pointed, so the
 * direction survives both a greyscale screen and a reader that only has the
 * text.
 */
const AllocationDelta = ({ delta }: { delta: number }) => {
  const theme = useTheme();

  if (delta === 0) {
    return (
      <Box component='span' sx={{ color: theme.color.onSurfaceVariant }}>
        —
      </Box>
    );
  }

  const up = delta > 0;
  const Arrow = up ? ArrowUpwardIcon : ArrowDownwardIcon;

  return (
    <Box
      component='span'
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '2px',
        color: up ? theme.color.positive : theme.color.negative,
        fontWeight: 'bold',
      }}
    >
      <Arrow sx={{ fontSize: '14px' }} aria-hidden />
      {`${up ? '+' : '−'}${round(Math.abs(delta), 2)}%`}
    </Box>
  );
};

export default ReviewAllocationsVisual;
